import { z } from 'zod';
import { DbLessonSchoolTypeSchema } from './db-lesson-school-type';

export const LESSON_ENTITY_NAME = 'lesson';

export const LocationSchema = z.object({
  explorer: z.string(),
  folder: z.string(),
});

export const UserContentPrivacySchema = z.enum(['public', 'private', 'protected']);
export const UserContentChannelRightsSchema = z.enum(['none', 'givesProFeatures']);
export const UserContentOriginSchema = z.object({
  id: z.string(),
  date: z.date(),
  unmodified: z.boolean().optional(),
  channel: z.string().nullish(),
  rights: UserContentChannelRightsSchema.optional(),
  rightsCheckDate: z.date().optional(),
});
export const FractionalIndexSchema = z.object({});

export const UserContentSchema = z.object({
  creationDate: z.date().or(z.string()),
  location: LocationSchema.nullish(),
  user: z.string(),
  privacy: UserContentPrivacySchema.optional(),
  removed: z.coerce.date().optional(),
  products: z.array(z.string()).nullish(),
  channel: z.string().nullish(),
  origin: UserContentOriginSchema.optional(),
  productsLocked: z.boolean().optional(),
  order: z.string().nullish(),
});

export const ShareOptionsSchema = z.object({
  sharePublic: z.boolean().optional(),
  shareDate: z.coerce.date().nullish(),
  publish: z.boolean().optional(),
  publishDate: z.date().or(z.string()).optional(),
});

export const SchoolTypeSchema = DbLessonSchoolTypeSchema.or(z.string());
export type SchoolType = z.infer<typeof SchoolTypeSchema>;
export const SchoolLevelSchema = z.enum(['m', 'h', 'v']).or(z.string());
export type SchoolLevel = z.infer<typeof SchoolLevelSchema>;

// https://github.com/colinhacks/zod/issues/2686
export const DiffColorNumberSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
]);
export const DiffCustomEntryLabelsSchema = z.enum([
  'custom-1',
  'custom-2',
  'custom-3',
  'custom-4',
  'custom-5',
  'custom-6',
]);
export const DiffNoPickedLabelsSchema = z.enum(['none-1', 'none-2', 'none-3', 'none-4', 'none-5', 'none-6']);
export const DiffLabelSchema = z.enum(['-', '+', '0']).or(DiffCustomEntryLabelsSchema).or(DiffNoPickedLabelsSchema);
export const DifferentiationSchema = z.object({
  color: DiffColorNumberSchema,
  label: DiffLabelSchema,
  custom: z.string().optional(),
});

export const LessonPhaseSchema = z.object({
  label: z.string(),
  subtitle: z.string().optional(),
});

export const LessonCurriculaRef = z.object({
  id: z.string(),
  label: z.string().optional(),
  objectives: z.array(z.string()),
});

export const LessonTrail = z.object({
  channel: z.string().optional(),
  lesson: z.string(),
  d: z.date().or(z.string()),
});

export const LessonThumbnail = z.object({
  modifiedDate: z.date().or(z.string()),
  url: z.string(),
  hash: z.string().optional(),
  v: z.number().optional(),
});

export const LessonAttachment = z.object({
  mimetype: z.string(),
  name: z.string(),
  type: z.enum(['worksheet', 'instruction']),
  uploadId: z.string().optional(),
});

export const LessonSourceSchema = z.enum(['openAi', 'slideDeckImport', 'lessonConversion']);

export const BaseLessonSchema = UserContentSchema.extend(
  z.object({
    _id: z.string(),
    name: z.string().min(1).max(300),
    country: z.array(z.string()),
    showPhases: z.boolean().nullish(),
    sharing: ShareOptionsSchema,
    schoolType: z.array(SchoolTypeSchema).nullish(),
    levels: z.array(SchoolLevelSchema).nullish(),
    years: z.array(z.number()).nullish(),
    subjects: z.array(z.string()).nullish(),
    themes: z.array(z.string()).optional(),
    pinCount: z.number(),
    pinCountForLesson: z.number().nullish(),
    pinCountForStudent: z.number().nullish(),
    modifiedDate: z.date().or(z.string()).optional(),
    openendDate: z.date().or(z.string()).optional(),
    durationInMin: z.number().nullish(),
    differentation: z.array(DifferentiationSchema).nullish(),
    sharedExplorer: z.string().optional(),
    isTest: z.boolean().nullish(),
    plan: z.string().nullish(),
    phases: z.array(LessonPhaseSchema).nullish(),
    isStub: z.boolean().optional(),
    curricula: z.array(LessonCurriculaRef).optional(),
    description: z.string().nullish(),
    information: z.string().nullish(),
    trail: z.array(LessonTrail).optional(),
    stats: z.record(z.string(), z.any()).optional(),
    thumbnail: LessonThumbnail.nullish(),
    attachments: z.array(LessonAttachment).nullish(),
    tags: z.array(z.string()).optional(),
    // TODO: templatePins
    cover: z.string().optional(),
    header: z.enum(['lessonup', 'coverbar']).optional(),
    coverFocusY: z.number().optional(),
    template: z.boolean().optional(),
    version: z.enum(['v2']).optional(),
    source: z.undefined().optional(),
    region: z.array(z.string()).optional(),
  }).shape
);

export const LessonConversionSchema = {
  source: z.literal(LessonSourceSchema.enum.lessonConversion),
  originalLessonId: z.string(),
};

export const SdiLessonSchema = {
  source: z.literal(LessonSourceSchema.enum.slideDeckImport),
};

export const OpenAiLessonSchema = {
  source: z.literal(LessonSourceSchema.enum.openAi),
};

/**
 * ⚠️ WARNING:
 * If you add a new schema to this discriminated union, you MUST also update `DbLessonWithoutLocalizeFieldsSchema`
 * in `packages/modules/lessons/src/indexed-lessons/db/models/db-indexed-lesson.ts` to include the new schema.
 * Failing to do so may cause runtime errors or unexpected behavior during the lessons's indexation.
 */
export const DbLessonSchema = z.discriminatedUnion('source', [
  BaseLessonSchema.extend(LessonConversionSchema),
  BaseLessonSchema.extend(SdiLessonSchema),
  BaseLessonSchema.extend(OpenAiLessonSchema),
  BaseLessonSchema,
]);

export type UserContentPrivacy = z.infer<typeof UserContentPrivacySchema>;
export type LessonPhase = z.infer<typeof LessonPhaseSchema>;
export type DbLesson = z.infer<typeof DbLessonSchema>;
export type LessonSource = z.infer<typeof LessonSourceSchema>;
export type DbLessonLocation = z.infer<typeof LocationSchema>;
export type Differentiation = z.infer<typeof DifferentiationSchema>;
